import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../services/api';
import useTokenRefresh from '../hooks/useTokenRefresh';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [sessionExpired, setSessionExpired] = useState(false);
  const [sessionExpiredReason, setSessionExpiredReason] = useState('expired');
  // Global timezone (persisted). Default: Buenos Aires
  const [timezone, setTimezone] = useState(
    localStorage.getItem('timezone') || 'America/Argentina/Buenos_Aires'
  );

  // Token refresh callbacks
  const handleTokenRefreshed = useCallback((newToken) => {
    console.log('✅ Token refreshed by automatic refresh system');
    setToken(newToken);
  }, []);

  const handleRefreshFailed = useCallback((error) => {
    console.warn('⚠️ Token refresh failed, session will expire soon:', error.message);
    // Let it expire naturally - the 401 interceptor will handle logout
  }, []);

  // Automatic token refresh hook
  useTokenRefresh(token, handleTokenRefreshed, handleRefreshFailed);

  // Definir logout primero con useCallback para evitar re-creación
  const logout = useCallback((reason = 'manual') => {
    // Limpiar todo el estado de autenticación
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userProfile');
    // Keep timezone on logout so next login keeps user's preference
    
    // Si no es logout manual, mostrar el diálogo
    if (reason !== 'manual') {
      setSessionExpired(true);
      setSessionExpiredReason(reason);
      sessionStorage.setItem('logoutReason', reason);
    } else {
      sessionStorage.clear();
    }
    
    // Limpiar headers de API
    delete api.defaults.headers.common['Authorization'];
    
    // Resetear estado
    setToken(null);
    setUser(null);
    
    // Limpiar cookies de Google si existen
    if (window.google?.accounts?.id) {
      window.google.accounts.id.disableAutoSelect();
    }
  }, []);

  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      verifyToken();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Listen for session expired events from the interceptor
  useEffect(() => {
    const handleSessionExpired = (event) => {
      const reason = event.detail?.reason || 'expired';
      logout(reason);
    };

    window.addEventListener('sessionExpired', handleSessionExpired);
    
    return () => {
      window.removeEventListener('sessionExpired', handleSessionExpired);
    };
  }, [logout]);

  const verifyToken = async () => {
    try {
      const response = await api.post('/api/v1/auth/verify-token', { token });
      if (response.data.valid) {
        setUser(response.data.username);
      } else {
        logout();
      }
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('password', password);
      
      const response = await api.post('/api/v1/auth/login', formData);
      const { access_token } = response.data;
      
      localStorage.setItem('token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser(username);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const loginWithKey = async (accessKey) => {
    try {
      const response = await api.post('/api/v1/auth/login-with-key', null, {
        params: { access_key: accessKey }
      });
      const { access_token } = response.data;
      
      localStorage.setItem('token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser('admin');
      
      // Limpiar el perfil de Google cuando se hace login con Access Code
      localStorage.removeItem('userProfile');
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Invalid access key' 
      };
    }
  };

  const handleSessionExpiredClose = () => {
    setSessionExpired(false);
    setSessionExpiredReason('expired');
  };

  const value = {
    user,
    setUser,
    token,
    login,
    loginWithKey,
    logout,
    loading,
    sessionExpired,
    sessionExpiredReason,
    handleSessionExpiredClose,
    timezone,
    setTimezone
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
